import { createAction } from 'redux-action';
import axios from 'axios';
import { get_service_endpoint } from '../../ServiceEndpoints.js';
import { AxiosCallError, AxiosCallRequest, AxiosCallSuccess } from './commonActions';
import { history } from '../../index';
import { setAssessment } from './InviteActions';
import {
  startTestSuccess,
  setCurrentSectionId,
  fetchTestSettings,
  fetchTestInfo,
  updateSectionStatus,
} from './AssessmentActions';
import serializeSections from '../../Utils/sectionsSerializer';
import determineWorkflow from '../../Utils/determineWorkflow';
import { SECTION_STATUS_IN_PROGRESS } from '../../const';

let copdgEp = get_service_endpoint('copdg');
let assessmentEp = get_service_endpoint('assessment');
let questionBankEp = get_service_endpoint('question-bank');

// Action creators
export const getPracticeTestsSuccess = createAction('GET_PRACTICE_TESTS_SUCCESS');
export const startPracticeTestSuccess = createAction('START_PRACTICE_TEST_SUCCESS');
export const getPracticeAssessmentsSuccess = createAction('GET_PRACTICE_ASSESSMENTS_SUCCESS');
export const getPracticeAssessmentResultSuccess = createAction(
  'GET_PRACTICE_ASSESSMENT_RESULT_SUCCESS',
);
export const getBatchQuestionsSuccess = createAction('GET_BATCH_QUESTIONS_SUCCESS');
export const getPracticeSubmissionsSuccess = createAction('GET_PRACTICE_SUBMISSIONS_SUCCESS');
export const getPracticeTestCasesSuccess = createAction('GET_PRACTICE_TEST_CASES_SUCCESS');
export const getPracticeRecordingsSuccess = createAction('GET_PRACTICE_RECORDINGS_SUCCESS');
export const setActiveQuestion = createAction('SET_ACTIVE_QUESTION');
export const setActiveSubmission = createAction('SET_ACTIVE_SUBMISSION');

// Thunk action to get batch questions from question bank
export const fetchBatchQuestions = questionIds => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    const url = `${questionBankEp}/v1/questions/batchGet?include=details,topic,options,tags,subtopic`;

    axios
      .post(url, {
        question_ids: questionIds,
      })
      .then(response => {
        const questions = response.data.data || response.data;
        const included = response.data.included || [];

        // Parse questions with all details
        const parsedQuestions = questions.map(question => {
          // Get question text from included data (admin portal style)
          const questionIncluded = included.find(
            item => item.type === 'questions' && item.id === question.id,
          );
          const questionText = questionIncluded ? questionIncluded.attributes.text : '';

          // Parse options using relationship IDs
          const optionIds = question.relationships?.options?.data?.map(opt => opt.id) || [];
          const options = included
            .filter(item => item.type === 'options' && optionIds.includes(item.id))
            .map(item => ({
              id: item.id,
              text: item.attributes.text,
              is_correct: item.attributes.is_correct,
              order: item.attributes.order || 0,
            }))
            .sort((a, b) => a.order - b.order);

          // Parse tags using relationship IDs
          const tagIds = [...new Set(question.relationships?.tags?.data?.map(tag => tag.id) || [])];
          const tags = included
            .filter(item => item.type === 'tags' && tagIds.includes(item.id))
            .map(item => ({
              id: item.id,
              name: item.attributes.name,
            }));

          // Parse topic and subtopic (admin portal style)
          let topic = null;
          let subtopic = null;
          const subtopicId = question.relationships?.topic?.data?.id;
          if (subtopicId) {
            // Get topic data from included array
            const topicDetails = included.find(
              item => item.type === 'topics' && item.id === subtopicId,
            );
            if (topicDetails) {
              // Check if this topic has a parent (subtopic)
              const parentId = topicDetails.relationships?.parent?.data?.id;
              if (parentId) {
                const parentTopic = included.find(
                  item => item.type === 'topics' && item.id === parentId,
                );
                if (parentTopic) {
                  topic = parentTopic.attributes.name;
                  subtopic = topicDetails.attributes.name;
                } else {
                  topic = topicDetails.attributes.name;
                }
              } else {
                topic = topicDetails.attributes.name;
              }
            }
          }

          return {
            id: question.id,
            question_text: questionText,
            title: questionText,
            text: questionText,
            difficulty: question.attributes.difficulty_level,
            points: question.attributes.points,
            multiple_correct: question.attributes.multiple_correct || false,
            format: questionIncluded.attributes.format || 'markdown',
            options: options,
            tags: tags,
            topic: topic,
            subtopic: subtopic,
            version: question.attributes.version || 1,
            type: 'mcq', // Add type for consistency
          };
        });

        dispatch(getBatchQuestionsSuccess(parsedQuestions));
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        console.error('fetchBatchQuestions - error:', error);
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to get practice assessment submissions for a specific question
export const fetchPracticeSubmissions = (assessmentId, problemId) => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    const url = `${assessmentEp}/api/assessments/${assessmentId}/problems/${problemId}/codesubmissions/`;

    axios
      .get(url)
      .then(response => {
        dispatch(
          getPracticeSubmissionsSuccess({
            question_id: problemId,
            data: response.data,
          }),
        );
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to get practice assessment test cases for a specific question
export const fetchPracticeTestCases = questionId => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    const url = `${copdgEp}/api/problems/${questionId}/test_cases/`;

    axios
      .get(url)
      .then(response => {
        dispatch(getPracticeTestCasesSuccess(response.data));
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to get practice assessment recordings
export const fetchPracticeRecordings = assessmentId => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    const url = `${assessmentEp}/api/assessments/${assessmentId}/recordings/urls`;

    axios
      .get(url)
      .then(response => {
        dispatch(
          getPracticeRecordingsSuccess({
            assessment_id: assessmentId,
            recordings: response.data,
          }),
        );
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to get a single practice assessment result
export const fetchPracticeAssessmentResult = assessmentId => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    const url = `${assessmentEp}/api/assessments/${assessmentId}/`;

    axios
      .get(url)
      .then(response => {
        dispatch(getPracticeAssessmentResultSuccess(response.data));

        // Extract MCQ ids from the assessment result
        const assessmentData = response.data;
        if (assessmentData && assessmentData.mcqs) {
          const mcqIds = assessmentData.mcqs.map(mcq => mcq.id);

          // Fetch batch questions from question bank
          if (mcqIds.length > 0) {
            dispatch(fetchBatchQuestions(mcqIds));
          }
        }

        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to fetch practice assessments
export const fetchPracticeAssessments = (testId = null) => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    const url = testId
      ? `${assessmentEp}/api/practice-assessments?test_id=${testId}`
      : `${assessmentEp}/api/practice-assessments/`;

    axios
      .get(url)
      .then(response => {
        dispatch(getPracticeAssessmentsSuccess(response.data));
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to fetch practice tests
export const fetchPracticeTests = () => {
  return dispatch => {
    dispatch(AxiosCallRequest());
    axios
      .get(`${copdgEp}/api/practice-tests/`)
      .then(response => {
        dispatch(getPracticeTestsSuccess(response.data));
        dispatch(AxiosCallSuccess());
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};

// Thunk action to start a practice test
export const startPracticeTest = (testId = '4f32a8bf-8ebd-4334-bb7d-df1463e89801') => {
  return (dispatch, getState) => {
    dispatch(AxiosCallRequest());
    axios
      .post(`${assessmentEp}/api/practice-test/${testId}/start/`)
      .then(response => {
        dispatch(startTestSuccess(response.data));
        dispatch(setAssessment(response.data));

        // Set currentSectionId to current section's ID if sections exist
        const { currentSection } = serializeSections(response.data.sections);
        if (currentSection) {
          dispatch(setCurrentSectionId(currentSection.id));

          // Start the first section if it exists
          dispatch(updateSectionStatus(currentSection.id, SECTION_STATUS_IN_PROGRESS))
            .then(sectionResponse => {
              // Determine workflow after starting the test
              determineWorkflow({
                sections: response.data.sections,
                problems: response.data.problems,
                mcqIds: response.data.mcqs,
                assessmentId: response.data.id,
                routerHistory: history,
                shouldRoute: true,
                currentSectionId: currentSection?.id,
              });
            })
            .catch(sectionError => {
              // Still proceed with workflow determination even if section start fails
              determineWorkflow({
                sections: response.data.sections,
                problems: response.data.problems,
                mcqIds: response.data.mcqs,
                assessmentId: response.data.id,
                routerHistory: history,
                shouldRoute: true,
                currentSectionId: currentSection?.id,
              });
            });
        } else {
          dispatch(AxiosCallSuccess());

          // Determine workflow if there are no sections
          determineWorkflow({
            sections: response.data.sections,
            problems: response.data.problems,
            mcqIds: response.data.mcqs,
            assessmentId: response.data.id,
            routerHistory: history,
            shouldRoute: true,
            currentSectionId: null,
          });
        }
      })
      .catch(error => {
        dispatch(AxiosCallError(error));
      });
  };
};
